import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { TbChevronLeft, TbCash, TbClock, TbMoneybag, TbFileText } from 'react-icons/tb';
import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
} from '../../components/tables/BaseTable';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { PayoutDetails, PayoutDetailsWithPayments } from '../../../services/model/marketplace.model';
import { getPayoutTransactionForBusiness } from '../../../services/orders.marketplace.service';

export default function MerchantPayoutPage(): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.merchantAccount' });
  const { payoutId } = useParams();

  const [isLoading, setIsLoading] = React.useState(false);
  const [payoutDetails, setPayoutDetails] = React.useState<PayoutDetailsWithPayments | null>(null);

  const loadPayoutDetails = async () => {
    if (!payoutId) return;
    setIsLoading(true);
    try {
      getPayoutTransactionForBusiness(payoutId).then((transactions) => {
        if (!transactions.length) {
          setPayoutDetails(null);
          return;
        }
        const payout: PayoutDetails = transactions.reduce((acc, tx) => {
          acc.id = tx.payoutId;
          acc.date = `${tx.payoutDate}`;
          acc.totalAmount += tx.netAmount;
          acc.totalIncomingTransactions += tx.faceValueAmount;
          acc.totalFees += (tx.stripeFeeAmount ?? 0) + (tx.veraFeeAmount ?? 0);
          acc.numberOfTransactions += 1;
          return acc;
        }, {
          id: '',
          date: '',
          totalAmount: 0,
          totalIncomingTransactions: 0,
          totalFees: 0,
          numberOfTransactions: 0,
        });


        setPayoutDetails({
          ...payout,
          individualPayments: transactions.map((tx) => ({
            id: tx.orderId,
            memberName: tx.customerName,
            product: `${tx.productName} (${tx.productType})`,
            received: tx.netAmount,
            stripeFee: tx.stripeFeeAmount ?? 0,
            veraFee: tx.veraFeeAmount ?? 0,
          }))
        })
      }).catch((error) => {
        console.error('Error fetching payout transactions:', error);
        setPayoutDetails(null);
      });
    } catch (error) {
      console.error('Error loading payout details:', error);
      setPayoutDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPayoutDetails();

  }, [payoutId]);

  if (isLoading || !payoutDetails) {
    return <></>;
  }

  return (
    <>
      <Link to="/financial/sales-collect" state={{ activeTab: 'paymentSettings' }}>
        <Button className="mb-2 gap-1 p-0">
          <TbChevronLeft size={20} /> Back
        </Button>
      </Link>

      <PageHeader
        title={`Payout Details - ${new Date(payoutDetails.date).toLocaleDateString(i18n.language)}`}
        subtitle={`Total Amount: ${formatEuroCentsWithSign(payoutDetails.totalAmount, i18n.language)}`}
      />

      {/* Payout Summary Cards */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Payout Summary</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Amount */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbCash className="h-6 w-6 text-primary-300" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Amount</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatEuroCentsWithSign(payoutDetails.totalAmount, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Total Incoming */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbMoneybag className="h-6 w-6 text-success" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Incoming</p>
                <p className="text-xl font-bold text-success">
                  {formatEuroCentsWithSign(
                    payoutDetails.totalIncomingTransactions,
                    i18n.language,
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Total Fees */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbFileText className="h-6 w-6 text-danger" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Fees</p>
                <p className="text-xl font-bold text-danger">
                  {formatEuroCentsWithSign(payoutDetails.totalFees, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          {/* Number of Transactions */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbClock className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Transactions</p>
                <p className="text-xl font-bold text-gray-900">
                  {payoutDetails.numberOfTransactions}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Individual Payments Table */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Individual Payments</h2>
        <BaseTable>
          <BaseTableHeader>
            <BaseTableHeaderRow>
              <th className="text-left">Member Name</th>
              <th className="text-left">Product</th>
              <th className="text-right">Received</th>
              <th className="text-right">Stripe Fee</th>
              <th className="text-right">Vera Fee</th>
            </BaseTableHeaderRow>
          </BaseTableHeader>
          <BaseTableBody>
            {payoutDetails.individualPayments.map((payment) => (
              <BaseTableDataRow
                key={payment.id}
                className="group transition-all hover:bg-secondary-200">
                <td className="py-3 text-left">
                  <span className="font-medium text-gray-900">{payment.memberName}</span>
                </td>
                <td className="py-3 text-left">
                  <span className="text-gray-700">{payment.product}</span>
                </td>
                <td className="py-3 text-right">
                  <span className="font-medium text-success">
                    {formatEuroCentsWithSign(payment.received, i18n.language)}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <span className="font-medium text-danger">
                    {formatEuroCentsWithSign(payment.stripeFee, i18n.language)}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <span className="font-medium text-secondary">
                    {formatEuroCentsWithSign(payment.veraFee, i18n.language)}
                  </span>
                </td>
              </BaseTableDataRow>
            ))}
          </BaseTableBody>
        </BaseTable>
      </div>
    </>
  );
}
